/**
 * The Workflow Builder (Slack's; docs/features/workflows.md "Builder (web)"): the workflows I manage, and an editor
 * for one: name, icon, trigger, steps (add, edit, reorder, delete, conditions), Save, Test run, Publish / Unpublish,
 * the webhook URL (shown once), managers, delete, and the activity log. Owners/admins also choose who can create
 * workflows.
 *
 * - **Drafts:** edits stay local until Save. A draft with empty required fields can't be saved (`incomplete`);
 *   Publish saves first and needs `workflowProblems` empty. Leaving with unsaved changes asks first (`confirmLeave`).
 * - **Test run** runs the saved working copy as me (`test: true`); its form opens on this client (the clientToken).
 * - Every change refreshes WorkspaceViewModel.workflows, so channel workflow menus and the "/" menu follow.
 * - **App steps:** opening the builder loads the installed apps (`apps`, `appChoices`); a new app step picks its app
 *   and declared step (`chooseAppStep`). Empty required inputs of a declared step keep the draft from saving.
 *
 * One per workspace shell (the web host); online only.
 */
import type { App, Workflow, WorkflowCreators, WorkflowDefinition, WorkflowRun, WorkflowStep, WorkflowStepType, WorkflowTrigger, WorkflowTriggerType } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { appStepBlocksSave, appStepChoices, appStepFromDeclaration, appStepIssues, blankDefinition, canSaveDraft, managedWorkflows, sameDefinition, switchTrigger, type AppStepChoice, type AppStepIssue } from "../logic/workflowBuilder.ts";
import { blankStep, moveStep, newStepId, workflowProblems, type WorkflowProblem } from "../logic/workflows.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type WorkflowEditor = {
  /** Absent: a new workflow not saved yet. */
  id?: string;
  /** The saved workflow (status, version, webhook). */
  workflow?: Workflow;
  draft: WorkflowDefinition;
  managers: string[];
  /** The draft or managers differ from what's saved. */
  dirty: boolean;
  /** What keeps the draft from publishing (`workflowProblems`). */
  problems: WorkflowProblem[];
  tab: "build" | "activity";
  runs?: WorkflowRun[];
  runsStatus?: "loading" | "ready" | "error";
  /** The webhook URL right after it was made (shown once, like Slack). */
  webhookUrl?: string;
  /** The step whose fields are open (Slack edits one step at a time). */
  openStep?: string;
};

export type WorkflowBusy = "save" | "publish" | "unpublish" | "test" | "delete" | "regenerate" | "creators";

export type WorkflowBuilderState = {
  open: boolean;
  /** The client has the workflows API. */
  supported: boolean;
  status: "idle" | "loading" | "ready" | "error";
  /** The workflows I manage, last edited first. */
  workflows: Workflow[];
  canCreate: boolean;
  creators: WorkflowCreators;
  /** Owners/admins choose who can create workflows. */
  canSetCreators: boolean;
  editor?: WorkflowEditor;
  busy?: WorkflowBusy;
  /** Leaving the editor (or closing) would drop unsaved changes: the UI asks. */
  confirmLeave?: "back" | "close";
  notice?: MessageDescriptor;
  /** Apps installed here (absent: not loaded yet, or the client can't list them). */
  apps?: App[];
  /** Installed apps that offer workflow steps, with their steps (the app step picker). */
  appChoices: AppStepChoice[];
};

const initial = (supported: boolean): WorkflowBuilderState => ({ open: false, supported, status: "idle", workflows: [], canCreate: false, creators: "everyone", canSetCreators: false, appChoices: [] });

export class WorkflowBuilderViewModel extends ViewModel<WorkflowBuilderState> {
  private saved: { draft: WorkflowDefinition; managers: string[] } | undefined;

  constructor(
    private readonly ws: WorkspaceViewModel,
    private readonly timeZone: () => string,
  ) {
    super(initial(ws.deps.api.listWorkflows !== undefined));
  }

  private get api() {
    return this.ws.deps.api;
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  /** Opens the builder: the list, a workflow (`workflowId`) or a new one in a channel (`newInChannel`). */
  open(opts: { workflowId?: string; newInChannel?: string } = {}): void {
    this.update({ open: true, notice: undefined, confirmLeave: undefined });
    void this.loadApps();
    if (opts.newInChannel !== undefined) this.create(opts.newInChannel);
    void this.refresh().then(() => {
      if (opts.workflowId !== undefined) this.edit(opts.workflowId);
      // Only owners/admins may create here: the new workflow goes away and the list says why.
      if (opts.newInChannel !== undefined && this.state.status === "ready" && !this.state.canCreate && this.state.editor?.id === undefined) {
        this.saved = undefined;
        this.update({ editor: undefined, notice: msg("workflow.notice.cantCreate") });
      }
    });
  }

  /** Closes the builder (asks first when there are unsaved changes, unless `force`). */
  close(force = false): void {
    if (!force && this.state.editor?.dirty === true) {
      this.update({ confirmLeave: "close" });
      return;
    }
    this.saved = undefined;
    this.update({ open: false, editor: undefined, confirmLeave: undefined, busy: undefined });
  }

  /** Back to the list (asks first when there are unsaved changes, unless `force`). */
  back(force = false): void {
    if (!force && this.state.editor?.dirty === true) {
      this.update({ confirmLeave: "back" });
      return;
    }
    this.saved = undefined;
    this.update({ editor: undefined, confirmLeave: undefined });
  }

  /** The "unsaved changes" question answered: leave (dropping them) or stay. */
  confirmLeave(leave: boolean): void {
    const what = this.state.confirmLeave;
    this.update({ confirmLeave: undefined });
    if (!leave || what === undefined) return;
    if (what === "close") this.close(true);
    else this.back(true);
  }

  async refresh(): Promise<void> {
    const list = this.api.listWorkflows;
    if (list === undefined) return;
    if (this.state.status !== "ready") this.update({ status: "loading" });
    try {
      const res = await list.call(this.api, this.ws.wsId);
      if (this.isDisposed) return;
      const me = this.ws.state.cache?.members[this.ws.state.meId];
      this.update({
        status: "ready",
        workflows: managedWorkflows(res.workflows),
        canCreate: res.canCreate,
        creators: res.creators,
        canSetCreators: me !== undefined && (me.role === "owner" || me.role === "admin"),
      });
      this.ws.loadWorkflows(true);
    } catch {
      if (!this.isDisposed && this.state.status !== "ready") this.update({ status: "error" });
    }
  }

  /** The apps installed here, for app steps (Slack lists each app's steps in "Add step"). */
  async loadApps(): Promise<void> {
    const list = this.api.listApps;
    if (list === undefined) return;
    try {
      const apps = await list.call(this.api, this.ws.wsId);
      if (!this.isDisposed) this.update({ apps, appChoices: appStepChoices(apps) });
    } catch {
      // The picker stays empty; app steps already in the draft keep their own copy.
    }
  }

  /** What's wrong with an app step (none until the apps are loaded, and for other steps). */
  appIssues(step: WorkflowStep): AppStepIssue[] {
    const apps = this.state.apps;
    return apps === undefined || step.type !== "app_step" || step.appId === "" ? [] : appStepIssues(step, apps);
  }

  /** Makes step `stepId` the app's declared step `callbackId` (its inputs empty, its outputs declared), keeping its condition. */
  chooseAppStep(stepId: string, appId: string, callbackId: string): void {
    const d = this.draft;
    const choice = this.state.appChoices.find((c) => c.appId === appId);
    const decl = choice?.steps.find((x) => x.callbackId === callbackId);
    const old = d?.steps.find((s) => s.id === stepId);
    if (d === undefined || decl === undefined || old === undefined) return;
    const step: WorkflowStep = old.condition === undefined ? appStepFromDeclaration(stepId, { id: appId }, decl) : { ...appStepFromDeclaration(stepId, { id: appId }, decl), condition: old.condition };
    this.updateStep(step);
  }

  private draftSavable(draft: WorkflowDefinition): boolean {
    return canSaveDraft(draft) && !draft.steps.some((s) => appStepBlocksSave(this.appIssues(s)));
  }

  // ---------- Editor ----------

  private startEditor(e: Omit<WorkflowEditor, "dirty" | "problems" | "tab">): void {
    this.saved = e.id === undefined ? undefined : { draft: e.draft, managers: e.managers };
    this.update({ editor: { ...e, dirty: e.id === undefined, problems: workflowProblems(e.draft, this.now()), tab: "build" }, notice: undefined, confirmLeave: undefined });
  }

  /** "New workflow": a link workflow in `channelId` (or none yet). */
  create(channelId = ""): void {
    this.startEditor({ draft: blankDefinition(channelId), managers: [] });
  }

  edit(workflowId: string): void {
    const wf = this.state.workflows.find((w) => w.id === workflowId);
    if (wf === undefined) return;
    this.startEditor({ id: wf.id, workflow: wf, draft: wf.definition, managers: wf.managers });
  }

  private setDraft(draft: WorkflowDefinition, extra: Partial<WorkflowEditor> = {}): void {
    const e = this.state.editor;
    if (e === undefined) return;
    this.patchEditor({ ...extra, draft, problems: workflowProblems(draft, this.now()) });
  }

  private patchEditor(patch: Partial<WorkflowEditor>): void {
    const e = this.state.editor;
    if (e === undefined) return;
    const next = { ...e, ...patch };
    next.dirty = this.saved === undefined || !sameDefinition(next.draft, this.saved.draft) || next.managers.join(",") !== this.saved.managers.join(",");
    this.update({ editor: next });
  }

  private get draft(): WorkflowDefinition | undefined {
    return this.state.editor?.draft;
  }

  setName(name: string): void {
    const d = this.draft;
    if (d !== undefined) this.setDraft({ ...d, name });
  }

  setDescription(description: string): void {
    const d = this.draft;
    if (d === undefined) return;
    const { description: _old, ...rest } = d;
    this.setDraft(description === "" ? rest : { ...rest, description });
  }

  setIcon(icon: string): void {
    const d = this.draft;
    if (d === undefined) return;
    const { icon: _old, ...rest } = d;
    this.setDraft(icon === "" ? rest : { ...rest, icon });
  }

  setTriggerType(type: WorkflowTriggerType): void {
    const d = this.draft;
    if (d !== undefined) this.setDraft({ ...d, trigger: switchTrigger(d.trigger, type, this.now(), this.timeZone()) });
  }

  setTrigger(trigger: WorkflowTrigger): void {
    const d = this.draft;
    if (d !== undefined) this.setDraft({ ...d, trigger });
  }

  /** Adds a blank step at the end and opens it. */
  addStep(type: WorkflowStepType): void {
    const d = this.draft;
    if (d === undefined) return;
    const step = blankStep(type, newStepId(d.steps), d.trigger);
    this.setDraft({ ...d, steps: [...d.steps, step] }, { openStep: step.id });
  }

  updateStep(step: WorkflowStep): void {
    const d = this.draft;
    if (d !== undefined) this.setDraft({ ...d, steps: d.steps.map((s) => (s.id === step.id ? step : s)) });
  }

  removeStep(stepId: string): void {
    const d = this.draft;
    if (d === undefined) return;
    const openStep = this.state.editor?.openStep;
    this.setDraft({ ...d, steps: d.steps.filter((s) => s.id !== stepId) }, openStep === stepId ? { openStep: undefined } : {});
  }

  /** Moves a step up (-1) or down (+1). */
  moveStep(stepId: string, by: number): void {
    const d = this.draft;
    if (d === undefined) return;
    const from = d.steps.findIndex((s) => s.id === stepId);
    const to = from + by;
    if (from < 0 || to < 0 || to >= d.steps.length) return;
    this.setDraft({ ...d, steps: moveStep(d.steps, from, to) });
  }

  /** Opens a step's fields (closing the open one), or closes it when it's open. */
  toggleStep(stepId: string): void {
    const e = this.state.editor;
    if (e !== undefined) this.patchEditor({ openStep: e.openStep === stepId ? undefined : stepId });
  }

  setManagers(managers: string[]): void {
    this.patchEditor({ managers });
  }

  showTab(tab: "build" | "activity"): void {
    this.patchEditor({ tab });
    if (tab === "activity") void this.loadRuns();
  }

  get canSave(): boolean {
    const e = this.state.editor;
    return e !== undefined && e.dirty && this.state.busy === undefined && this.draftSavable(e.draft);
  }

  get canPublish(): boolean {
    const e = this.state.editor;
    return e !== undefined && this.state.busy === undefined && this.draftSavable(e.draft) && e.problems.length === 0 && (e.dirty || e.workflow?.published !== true || e.workflow.changed === true);
  }

  private fail(notice: MessageDescriptor): void {
    if (!this.isDisposed) this.update({ busy: undefined, notice });
  }

  /** The editor shows `wf` as saved (keeping what's open). */
  private savedAs(wf: Workflow, extra: Partial<WorkflowEditor> = {}): void {
    this.saved = { draft: wf.definition, managers: wf.managers };
    const e = this.state.editor;
    if (e === undefined) return;
    const editor: WorkflowEditor = { ...e, ...extra, id: wf.id, workflow: wf, draft: wf.definition, managers: wf.managers, dirty: false, problems: workflowProblems(wf.definition, this.now()) };
    const others = this.state.workflows.filter((w) => w.id !== wf.id);
    this.update({ editor, workflows: managedWorkflows([wf, ...others]), busy: undefined });
  }

  /** Saves the draft (creates the workflow the first time). Resolves with the saved workflow. */
  async save(): Promise<Workflow | undefined> {
    const e = this.state.editor;
    if (e === undefined || this.state.busy !== undefined || !this.draftSavable(e.draft)) return undefined;
    if (!e.dirty && e.workflow !== undefined) return e.workflow;
    const create = this.api.createWorkflow;
    const update = this.api.updateWorkflow;
    if (create === undefined || update === undefined) return undefined;
    this.update({ busy: "save", notice: undefined });
    try {
      const wf = e.id === undefined ? await create.call(this.api, this.ws.wsId, { definition: e.draft, managers: e.managers }) : await update.call(this.api, this.ws.wsId, e.id, { definition: e.draft, managers: e.managers });
      if (this.isDisposed) return undefined;
      this.savedAs(wf);
      this.update({ notice: msg("workflow.notice.saved") });
      this.ws.loadWorkflows(true);
      return wf;
    } catch {
      this.fail(msg("workflow.notice.saveFailed"));
      return undefined;
    }
  }

  /** Saves, then publishes; a webhook trigger's new URL shows once. */
  async publish(): Promise<void> {
    if (!this.canPublish) return;
    const publish = this.api.publishWorkflow;
    if (publish === undefined) return;
    const wf = await this.save();
    if (wf === undefined) return;
    this.update({ busy: "publish", notice: undefined });
    try {
      const res = await publish.call(this.api, this.ws.wsId, wf.id);
      if (this.isDisposed) return;
      this.savedAs(res.workflow, res.webhookUrl === undefined ? {} : { webhookUrl: res.webhookUrl });
      this.update({ notice: msg("workflow.notice.published") });
      this.ws.loadWorkflows(true);
    } catch {
      this.fail(msg("workflow.notice.publishFailed"));
    }
  }

  async unpublish(): Promise<void> {
    const id = this.state.editor?.id;
    const unpublish = this.api.unpublishWorkflow;
    if (id === undefined || unpublish === undefined || this.state.busy !== undefined) return;
    this.update({ busy: "unpublish", notice: undefined });
    try {
      const wf = await unpublish.call(this.api, this.ws.wsId, id);
      if (this.isDisposed) return;
      this.keepDraft(wf);
      this.update({ notice: msg("workflow.notice.unpublished") });
      this.ws.loadWorkflows(true);
    } catch {
      this.fail(msg("workflow.notice.failed"));
    }
  }

  /** The saved workflow changed on the server (status, URL) without touching my unsaved edits. */
  private keepDraft(wf: Workflow, extra: Partial<WorkflowEditor> = {}): void {
    const e = this.state.editor;
    if (e === undefined) return;
    if (!e.dirty) {
      this.savedAs(wf, extra);
      return;
    }
    const others = this.state.workflows.filter((w) => w.id !== wf.id);
    this.update({ editor: { ...e, ...extra, workflow: wf }, workflows: managedWorkflows([wf, ...others]), busy: undefined });
  }

  /** A new webhook URL (the old one stops working); shown once. */
  async regenerateHook(): Promise<void> {
    const id = this.state.editor?.id;
    const regenerate = this.api.regenerateWorkflowHook;
    if (id === undefined || regenerate === undefined || this.state.busy !== undefined) return;
    this.update({ busy: "regenerate", notice: undefined });
    try {
      const res = await regenerate.call(this.api, this.ws.wsId, id);
      if (this.isDisposed) return;
      this.keepDraft(res.workflow, res.webhookUrl === undefined ? {} : { webhookUrl: res.webhookUrl });
    } catch {
      this.fail(msg("workflow.notice.failed"));
    }
  }

  dismissWebhookUrl(): void {
    this.patchEditor({ webhookUrl: undefined });
  }

  /** Saves, then runs the working copy as me; its form opens here. Shows the activity log afterwards. */
  async testRun(): Promise<void> {
    const run = this.api.runWorkflow;
    if (run === undefined || this.state.busy !== undefined) return;
    const wf = await this.save();
    if (wf === undefined) return;
    this.update({ busy: "test", notice: undefined });
    try {
      const t = wf.definition.trigger;
      await run.call(this.api, this.ws.wsId, wf.id, { test: true, clientToken: this.ws.modal.newClientToken(), ...("channelId" in t ? { channelId: t.channelId } : {}) });
      if (this.isDisposed) return;
      this.update({ busy: undefined, notice: msg("workflow.notice.testStarted") });
      void this.loadRuns();
    } catch {
      this.fail(msg("workflow.notice.testFailed"));
    }
  }

  async loadRuns(): Promise<void> {
    const id = this.state.editor?.id;
    const list = this.api.listWorkflowRuns;
    if (id === undefined || list === undefined) return;
    if (this.state.editor?.runs === undefined) this.patchEditor({ runsStatus: "loading" });
    try {
      const runs = await list.call(this.api, this.ws.wsId, id);
      if (!this.isDisposed && this.state.editor?.id === id) this.patchEditor({ runs, runsStatus: "ready" });
    } catch {
      if (!this.isDisposed && this.state.editor?.id === id) this.patchEditor({ runsStatus: this.state.editor.runs === undefined ? "error" : "ready" });
    }
  }

  /** Deletes the workflow (or drops a new one never saved) and goes back to the list. */
  async remove(): Promise<void> {
    const e = this.state.editor;
    if (e === undefined || this.state.busy !== undefined) return;
    if (e.id === undefined) {
      this.back(true);
      return;
    }
    const del = this.api.deleteWorkflow;
    if (del === undefined) return;
    const id = e.id;
    this.update({ busy: "delete", notice: undefined });
    try {
      await del.call(this.api, this.ws.wsId, id);
      if (this.isDisposed) return;
      this.saved = undefined;
      this.update({ busy: undefined, editor: undefined, workflows: this.state.workflows.filter((w) => w.id !== id), notice: msg("workflow.notice.deleted") });
      this.ws.loadWorkflows(true);
    } catch {
      this.fail(msg("workflow.notice.failed"));
    }
  }

  /** Owners/admins: who can create workflows. */
  async setCreators(creators: WorkflowCreators): Promise<void> {
    const set = this.api.updateWorkflowSettings;
    if (set === undefined || !this.state.canSetCreators || this.state.busy !== undefined || creators === this.state.creators) return;
    const before = this.state.creators;
    this.update({ busy: "creators", creators, notice: undefined });
    try {
      const saved = await set.call(this.api, this.ws.wsId, { creators });
      if (!this.isDisposed) this.update({ busy: undefined, creators: saved });
    } catch {
      if (!this.isDisposed) this.update({ creators: before });
      this.fail(msg("workflow.notice.failed"));
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
